import React from 'react';
import { useTranslation } from 'react-i18next';
import { User, UserRole } from '../types';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';

interface UsersDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onEdit: () => void;
  canManage: boolean;
}

export const UsersDetailModal: React.FC<UsersDetailModalProps> = ({
  isOpen,
  onClose,
  user,
  onEdit,
  canManage,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'tr' ? tr : enUS;

  if (!isOpen || !user) return null;

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return '-';
    return format(new Date(date), 'dd MMM yyyy HH:mm', { locale });
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case UserRole.PLATFORM_ADMIN:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case UserRole.ORG_ADMIN:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case UserRole.MANAGER:
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case UserRole.STAFF:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case UserRole.PROVIDER_USER:
      case UserRole.PROVIDER_MANAGER:
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.PLATFORM_ADMIN:
        return t('users.roles.platformAdmin');
      case UserRole.ORG_ADMIN:
        return t('users.roles.orgAdmin');
      case UserRole.MANAGER:
        return t('users.roles.manager');
      case UserRole.STAFF:
        return t('users.roles.staff');
      case UserRole.PROVIDER_USER:
        return t('users.roles.providerUser');
      case UserRole.PROVIDER_MANAGER:
        return t('users.roles.providerManager');
      default:
        return role;
    }
  };

  const DetailRow = ({ label, value, className = '' }: { label: string; value: React.ReactNode; className?: string }) => (
    <div className={`py-3 border-b border-gray-100 dark:border-gray-700 ${className}`}>
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-white">{value || '-'}</dd>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <span className="text-indigo-600 dark:text-indigo-400 font-semibold">
                {user.fullName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {user.fullName}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                  {getRoleLabel(user.role)}
                </span>
                {user.provider && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-700">
                    {user.provider.name}
                  </span>
                )}
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  user.isActive
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {user.isActive ? t('common.active') : t('common.inactive')}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <dl className="space-y-1">
            <DetailRow
              label={t('users.email')}
              value={user.email}
            />
            <DetailRow
              label={t('users.role')}
              value={getRoleLabel(user.role)}
            />
            {user.provider && (
              <DetailRow
                label={t('providers.title')}
                value={
                  <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 rounded">
                    {user.provider.name}
                  </span>
                }
              />
            )}
            {user.assignedProviders && user.assignedProviders.length > 0 && (
              <DetailRow
                label={t('users.assignedProviders')}
                value={
                  <div className="flex flex-wrap gap-1 mt-1">
                    {user.assignedProviders.map((provider) => (
                      <span
                        key={provider.id}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded"
                      >
                        {provider.name}
                      </span>
                    ))}
                  </div>
                }
              />
            )}
            {user.transferPermissions && user.transferPermissions.length > 0 && (
              <DetailRow
                label={t('users.transferPermissions')}
                value={
                  <div className="flex flex-wrap gap-1 mt-1">
                    {user.transferPermissions.map((permission) => (
                      <span
                        key={permission}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded"
                      >
                        {t(`transfers.status.${permission}`)}
                      </span>
                    ))}
                  </div>
                }
              />
            )}
            <DetailRow
              label={t('common.createdAt')}
              value={formatDate(user.createdAt)}
            />
            <DetailRow
              label={t('users.lastLogin')}
              value={user.lastLoginAt ? formatDate(user.lastLoginAt) : '-'}
              className="border-b-0"
            />
          </dl>
        </div>

        {/* Footer */}
        {canManage && (
          <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg">
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('common.edit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
